/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { EstimateCostDatabaseItemsGridNew as EstimateCostDatabaseItems } from "@Features/estimation";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { itemListSources } from "@Actions/estimation";
import { getDefaultCurrencyDetailForProjectLevel } from "@Actions/Currency";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { CurrencyDetail, SymbolDetail } from "@ServerTypes/currency/currency.schema";
import type { EstimateCurrency } from "@ServerTypes/estimates/estimates.schema";

/**
 * A selected BOQ row as this modal reads it. `EstimateCostDatabaseItemsGridNew` does not export its
 * `EstimateItem` row type, so the fields read here are restated with the same names, optionality and
 * types it declares — that keeps `EstimateItem[]` assignable to this, so the grid's real handle still
 * satisfies `BoqGridHandle` below.
 */
interface BoqSelectionRow {
  itemId: ObjectIdString;
  __isDummy?: boolean;
  isAlreadyAddedInThisContract?: boolean;
  isAlreadyAddedInThisForm?: boolean;
  isAlreadyAddedInThisTask?: boolean;
  availableQuantity?: number;
  parentsList?: Array<{ itemId?: string; parentId?: string; type?: string }>;
}

/** The slice of the BOQ grid's imperative handle (`TreeGridRef`, not exported) this modal drives. */
interface BoqGridHandle {
  dataView: { getAllSelectedItems: () => BoqSelectionRow[] };
}

/** One BOQ item as this modal hands it back to whichever caller opened it. */
export interface LinkedBoqItem {
  /**
   * The quantity to link: the row's `availableQuantity` when auto-populating, otherwise the user's
   * entry. `number | undefined` rather than `number` because `availableQuantity` is optional on the
   * grid row, so the auto-populate branch really can yield `undefined`.
   */
  customQuantity: number | undefined;
  itemId: ObjectIdString;
  parentsList?: BoqSelectionRow["parentsList"];
}

/** The project currency pair returned by `getDefaultCurrencyDetailForProjectLevel`; `{}` until loaded. */
interface ProjectCurrencyState {
  currencyDetail?: CurrencyDetail;
  symbolDetail?: SymbolDetail;
}

interface LinkBoqToTaskAndFormProps {
  showBoqModal: boolean;
  setShowBoqModal: (state: boolean) => void;
  /** Which caller opened the modal — the Gantt task linker or the form Quantities section. */
  source: "gantt" | "forms";
  addBoqItemsCallBack: (args: { boqItems: LinkedBoqItem[]; autoPopulateState?: boolean }) => void;
  contractId?: string;
  formId?: string;
  selectedTask?: { originalTaskId?: string };
  /** Passed by the Quantities section but not read here; declared so that call site type-checks. */
  formResourceType?: string;
  /** Passed by the Gantt caller but not read here; declared so that call site type-checks. */
  successCallback?: () => void;
}

function LinkBoqToTaskAndForm(props: Readonly<LinkBoqToTaskAndFormProps>) {
  const { t } = useTranslation();
  // Route params enter from outside the type system; the backend route schema validates these ids.
  const params = useParams<{ projectId: ObjectIdString; contractId: ObjectIdString }>();
  const boqItemsRef = useRef<BoqGridHandle | null>(null);
  const dispatch = useDispatch();
  const contractDetails = useSelector((state: RootState) => state.contractDetails);

  const estimateId = contractDetails.linkedEstimateVersion?.estimateId;
  const estimateVersionId = contractDetails.linkedEstimateVersion?.versionId;
  const contractId = params.contractId || props.contractId;
  const [currencyData, setCurrencyData] = useState<ProjectCurrencyState>({});
  const [linkBoqItemBtnState, setLinkBoqItemBtnState] = useState(false);
  const [customBoqQuantity, setCustomBoqQuantity] = useState<Record<string, number>>({});
  const [autoPopulateState, setAutoPopulateState] = useState(false);

  const setInitialData = useCallback(async () => {
    const response = await getDefaultCurrencyDetailForProjectLevel({
      projectId: params.projectId!,
    });
    if (response.data.status) {
      setCurrencyData({
        currencyDetail: response.data.currencyDetail,
        symbolDetail: response.data.symbolDetail,
      });
    }
  }, [params.projectId]);

  useEffect(() => {
    setInitialData();
  }, [setInitialData]);

  const handleLinkBoqItemsClick = async () => {
    // Non-null on the ref: the Link button that calls this renders in the modal footer alongside the
    // grid, so the ref is attached before it can fire. Erased at emit — an unattached ref still
    // throws exactly as it does today.
    const selectedBoqData = [
      ...boqItemsRef.current!.dataView.getAllSelectedItems().filter((record) => record && !record.__isDummy),
    ];
    let isAlreadyAdded = false;
    const boqItems = selectedBoqData.map((data) => {
      if (data.isAlreadyAddedInThisContract || data.isAlreadyAddedInThisForm || data.isAlreadyAddedInThisTask) {
        isAlreadyAdded = true;
      }
      const quantity = autoPopulateState ? data.availableQuantity : (customBoqQuantity[data.itemId] ?? 0);

      return {
        customQuantity: quantity,
        itemId: data.itemId,
        parentsList: data.parentsList,
      };
    });
    if (isAlreadyAdded) {
      dispatch(
        showToastNotification({
          content: t("One of the selected Item(s) is already added"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (!boqItems.length) {
      dispatch(
        showToastNotification({
          content: t("No BOQ Items have been selected"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else if (props.source == "gantt") {
      props.addBoqItemsCallBack({ boqItems });
    } else if (props.source == "forms") {
      props.addBoqItemsCallBack({ boqItems, autoPopulateState });
    }
  };

  const handleCancelBoqClick = () => {
    props.setShowBoqModal(false);
  };

  const boqHeaderContent = () => (
    <div className="  w-full flex justify-between rounded-md">
      <div className="">{t("BOQ Items")}</div>
    </div>
  );

  const setBtnState = (state: boolean) => {
    setLinkBoqItemBtnState(state);
  };

  const getCustomBoqQuantity = ({ itemId, value }: { itemId: string; value: number }) => {
    setCustomBoqQuantity((prev) => ({ ...prev, [itemId]: value }));
  };

  const boqModelContent = useCallback(
    () =>
      currencyData.currencyDetail && (
        <div className="mb-6 ms-6 me-6 flex overflow-hidden h-full">
          <EstimateCostDatabaseItems
            contractId={contractId}
            contractVersionId={contractDetails.versionId}
            estimateVersionId={estimateVersionId}
            estimateId={estimateId}
            getCustomBoqQuantity={getCustomBoqQuantity}
            setBtnState={setBtnState}
            // The grid's ref target (`TreeGridRef`) is not exported, and `BoqGridHandle` above is a
            // deliberate narrow duck-type of it, so the ref object needs bridging. Erased at emit.
            ref={boqItemsRef as unknown as React.ComponentProps<typeof EstimateCostDatabaseItems>["ref"]}
            enableCheckboxSelector
            source={itemListSources.LINK_BOQ_ITEMS}
            // KNOWN BUG, preserved verbatim: the grid's `currencyData` prop is `EstimateCurrency`
            // (`{ id, selectedSymbol: { name } }`), but this literal supplies `_id` and `name`
            // instead — and `currencyDetail` has no `_id` at all (`currencyDetailSchema` declares
            // `id`), so `_id` is `undefined` even before the key mismatch. The grid only reads
            // `currencyData.selectedSymbol.name`, which is why nothing visibly breaks today.
            // Asserted (erased at emit) to keep the existing runtime; renaming the keys would change
            // what the grid receives — a behaviour change, out of scope for a typing pass.
            currencyData={
              {
                _id: (currencyData.currencyDetail as CurrencyDetail & { _id?: string })._id,
                name: currencyData.currencyDetail.name,
                selectedSymbol: currencyData.symbolDetail,
              } as unknown as EstimateCurrency
            }
            childParentMapper={{
              itemId: "itemId",
              parentId: "parentId",
            }}
            taskLinking={props.source == "gantt"}
            formLinking={props.source == "forms"}
            taskId={props.selectedTask?.originalTaskId}
            formId={props.formId}
            setAutoPopulateState={setAutoPopulateState}
          />
        </div>
      ),

    [
      currencyData,
      linkBoqItemBtnState,
      customBoqQuantity,
      contractDetails.versionId,
      contractId,
      estimateId,
      estimateVersionId,
      props.formId,
      props.selectedTask?.originalTaskId,
      props.source,
    ],
  );

  const boqFooterContent = () => (
    <div className=" flex gap-4">
      <ButtonComponent
        disabled={!linkBoqItemBtnState}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleLinkBoqItemsClick}
      >
        {t("Link")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCancelBoqClick}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  const getBoqItemsList = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={boqFooterContent()}
      width="80%"
      height="80%"
      headerTitle={boqHeaderContent()}
      onCancel={handleCancelBoqClick}
      isVisible={props.showBoqModal}
      modelContent={boqModelContent()}
    />
  );

  const noEstimateLinkedWarningModal = () => (
    <ModalStateDialogComponent
      footerContent={<div />}
      width="30%"
      headerTitle={
        <div className="flex justify-between items-center w-full">
          <span>{t("No Estimate Linked")}</span>
        </div>
      }
      onCancel={handleCancelBoqClick}
      isVisible={props.showBoqModal}
      modelContent={<div className="">{t("No Estimate have been attached to this contract.")}</div>}
    />
  );

  return <div>{props.showBoqModal && estimateVersionId ? getBoqItemsList() : noEstimateLinkedWarningModal()}</div>;
}
export default LinkBoqToTaskAndForm;
